import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { useArtists } from '../hooks/useFirestore';
import { LayoutDashboard, Target, Calendar, Music, BarChart2, Users, FileText, LogOut, BookOpen } from 'lucide-react';

// ── War Games Icon — three swords, iron sharpens iron ─────────
function WarGamesIcon({ size = 16, style = {} }) {
  const s = size;
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, ...style }}>
      {/* Flame glow behind */}
      <ellipse cx="12" cy="14" rx="4" ry="5" fill="url(#flameGrad)" opacity="0.35" />
      {/* Left sword */}
      <line x1="7" y1="3" x2="11" y2="13" stroke="#e8621a" strokeWidth="1.5" strokeLinecap="round" />
      <polygon points="6,2 8,2 7,4" fill="#e8621a" opacity="0.9" />
      {/* Right sword */}
      <line x1="17" y1="3" x2="13" y2="13" stroke="#e8621a" strokeWidth="1.5" strokeLinecap="round" />
      <polygon points="16,2 18,2 17,4" fill="#e8621a" opacity="0.9" />
      {/* Center sword pointing up */}
      <line x1="12" y1="2" x2="12" y2="13" stroke="#f0a030" strokeWidth="2" strokeLinecap="round" />
      <polygon points="11,1 13,1 12,3" fill="#f0a030" />
      {/* Crossguards */}
      <line x1="5.5" y1="12" x2="8.5" y2="12" stroke="#e8621a" strokeWidth="1.2" strokeLinecap="round" />
      <line x1="15.5" y1="12" x2="18.5" y2="12" stroke="#e8621a" strokeWidth="1.2" strokeLinecap="round" />
      <line x1="10.5" y1="13.5" x2="13.5" y2="13.5" stroke="#f0a030" strokeWidth="1.4" strokeLinecap="round" />
      {/* Flame */}
      <path d="M12 24 C10 21 8 19 9 16 C10 14 12 15 12 17 C12 15 14 13 15 15 C16 18 14 21 12 24Z" fill="url(#flameGrad)" opacity="0.85" />
      <defs>
        <radialGradient id="flameGrad" cx="50%" cy="80%" r="60%">
          <stop offset="0%" stopColor="#f0a030" />
          <stop offset="100%" stopColor="#e8621a" stopOpacity="0" />
        </radialGradient>
      </defs>
    </svg>
  );
}

const NAV = [
  { section:'Overview', items:[{to:'/dashboard',icon:LayoutDashboard,label:'Dashboard'}]},
  { section:'Hypersigil ✶', items:[{to:'/hypersigil',icon:FileText,label:'Hypersigil ✶'}]},
  { section:'Music', items:[{to:'/music',icon:Music,label:'Music Garden'}]},
  { section:'Protocols', items:[{to:'/vocal',icon:FileText,label:'Protocols'}]},
  { section:'Content', items:[{to:'/content',icon:Calendar,label:'Content'}]},
  { section:'Ads', items:[{to:'/ads',icon:BarChart2,label:'Ad Strategy'}]},
];

export default function Sidebar({ activeArtistId, setActiveArtistId }) {
  const { profile, logout, isManager } = useAuth();
  const { artists } = useArtists();
  const [collapsed, setCollapsed] = useState(true);

  const navStyle = (isActive) => ({
    display:'flex', alignItems:'center', gap:9, padding:'8px 10px', borderRadius:7,
    color:isActive?'rgba(210,235,245,.95)':'rgba(120,160,190,.55)', fontSize:13, fontWeight:500,
    background:isActive?'rgba(60,120,160,.2)':'transparent',
    border:`1px solid ${isActive?'rgba(80,160,200,.25)':'transparent'}`,
    marginBottom:1, textDecoration:'none', transition:'all .16s',
    whiteSpace:'nowrap', overflow:'hidden',
  });

  return (
    <div style={{ position:'relative', display:'flex', flexShrink:0, zIndex:100, height:'100vh' }}>

      {/* WeMojave logo — always visible, click to toggle sidebar */}
      <div onClick={() => setCollapsed(v => !v)}
        style={{
          position:'sticky', top:0, height:'100vh', width:48,
          display:'flex', flexDirection:'column', alignItems:'center',
          justifyContent:'flex-start', paddingTop:24,
          cursor:'pointer', flexShrink:0, zIndex:102,
          background:'linear-gradient(180deg, rgba(4,10,18,.95) 0%, rgba(3,8,14,.98) 100%)',
          borderRight: collapsed ? 'none' : 'none',
        }}>
        {/* Vertical WeMojave text */}
        <div style={{
          writingMode:'vertical-rl',
          transform:'rotate(180deg)',
          fontSize:10, fontWeight:800, letterSpacing:4,
          color:'rgba(180,220,235,.7)',
          fontFamily:'var(--font)',
          textTransform:'uppercase', userSelect:'none',
          transition:'color .2s',
        }}
          onMouseEnter={e => e.currentTarget.style.color='rgba(220,240,250,.95)'}
          onMouseLeave={e => e.currentTarget.style.color='rgba(180,220,235,.7)'}
        >
          WeMojave
        </div>
      </div>

      {/* Sidebar panel — slides in from left */}
      <aside style={{
        width: collapsed ? 0 : 230,
        minWidth: collapsed ? 0 : 230,
        background:'linear-gradient(180deg, rgba(6,14,24,.97) 0%, rgba(4,10,18,.99) 100%)',
        borderRight:'none',
        display:'flex', flexDirection:'column',
        height:'100vh',
        overflowY: collapsed ? 'hidden' : 'auto',
        overflowX:'hidden',
        scrollbarWidth:'none',
        transition:'width .3s ease, min-width .3s ease',
        boxShadow: collapsed ? 'none' : '4px 0 24px rgba(0,0,0,.4), 1px 0 0 rgba(60,100,130,.15)',
      }}>

        {/* Soft fade on right edge */}
        {!collapsed && (
          <div style={{ position:'absolute', top:0, right:0, width:20, height:'100%', background:'linear-gradient(90deg, transparent, rgba(4,10,18,.3))', pointerEvents:'none', zIndex:1 }}/>
        )}

        <div style={{ padding:'24px 20px 18px', borderBottom:'1px solid rgba(60,100,130,.2)', flexShrink:0 }}>
          <div style={{ fontSize:18, fontWeight:800, marginBottom:4, color:'rgba(200,230,240,.9)', letterSpacing:-0.3 }}>WeMojave</div>
          <div style={{ fontSize:9, letterSpacing:3, textTransform:'uppercase', color:'rgba(80,120,150,.5)', fontFamily:'var(--mono)', marginBottom:16 }}>Artist OS</div>
          {isManager&&(
            <>
              <div style={{ fontSize:9, letterSpacing:2.5, textTransform:'uppercase', color:'rgba(80,120,150,.4)', fontFamily:'var(--mono)', marginBottom:8 }}>Viewing</div>
              <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                <button onClick={()=>setActiveArtistId(null)} style={{ flex:1, padding:'7px 8px', borderRadius:7, border:`1px solid ${!activeArtistId?'rgba(80,160,200,.4)':'rgba(60,100,130,.25)'}`, background:!activeArtistId?'rgba(60,120,160,.2)':'transparent', color:!activeArtistId?'rgba(200,230,240,.9)':'rgba(100,150,180,.5)', fontSize:11, fontWeight:600, cursor:'pointer', fontFamily:'var(--font)' }}>All</button>
                {artists.map(a=><button key={a.id} onClick={()=>setActiveArtistId(a.id)} style={{ flex:1, padding:'7px 8px', borderRadius:7, border:`1px solid ${activeArtistId===a.id?'rgba(80,160,200,.4)':'rgba(60,100,130,.25)'}`, background:activeArtistId===a.id?'rgba(60,120,160,.2)':'transparent', color:activeArtistId===a.id?'rgba(200,230,240,.9)':'rgba(100,150,180,.5)', fontSize:11, fontWeight:600, cursor:'pointer', fontFamily:'var(--font)' }}>{a.name}</button>)}
              </div>
            </>
          )}
          {!isManager&&<div style={{ padding:'8px 12px', borderRadius:8, background:'rgba(60,120,160,.15)', border:'1px solid rgba(80,160,200,.2)', fontSize:12, fontWeight:600, color:'rgba(180,215,230,.8)' }}>{artists.find(a=>a.id===profile?.artistId)?.name||'Artist'}</div>}
        </div>

        <nav style={{ padding:'14px 10px', flex:1 }}>
          {NAV.map(section=>(
            <div key={section.section} style={{ marginBottom:20 }}>
              <div style={{ fontSize:9, letterSpacing:2.5, textTransform:'uppercase', color:'rgba(70,110,140,.5)', padding:'0 10px 8px', fontFamily:'var(--mono)' }}>{section.section}</div>
              {section.items.map(({to,icon:Icon,label})=>(
                <NavLink key={to} to={to} style={({isActive})=>navStyle(isActive)}>
                  <Icon size={16} style={{ opacity:.75, flexShrink:0 }}/>{label}
                </NavLink>
              ))}
            </div>
          ))}
          <div style={{ marginBottom:20 }}>
            <div style={{ fontSize:9, letterSpacing:2.5, textTransform:'uppercase', color:'rgba(70,110,140,.5)', padding:'0 10px 8px', fontFamily:'var(--mono)' }}>War Games</div>
            <NavLink to="/wargames" style={({isActive})=>({
              ...navStyle(isActive),
              background: isActive ? 'linear-gradient(135deg,rgba(232,98,26,.2),rgba(240,160,48,.08))' : 'transparent',
              border: `1px solid ${isActive ? 'rgba(232,98,26,.5)' : 'transparent'}`,
            })}>
              <WarGamesIcon size={16} />
              <span style={{ background:'linear-gradient(90deg,#f0a030,#e8621a)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', fontWeight:600 }}>Game Console</span>
            </NavLink>
          </div>
          {isManager&&(
            <div style={{ marginBottom:20 }}>
              <div style={{ fontSize:9, letterSpacing:2.5, textTransform:'uppercase', color:'rgba(70,110,140,.5)', padding:'0 10px 8px', fontFamily:'var(--mono)' }}>Manager</div>
              <NavLink to="/team" style={({isActive})=>navStyle(isActive)}><Users size={16} style={{ opacity:.75 }}/>Team & Artists</NavLink>
            </div>
          )}
        </nav>

        <div style={{ padding:14, borderTop:'1px solid rgba(60,100,130,.15)', flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            {profile?.photoURL?<img src={profile.photoURL} alt="" style={{ width:32,height:32,borderRadius:'50%',flexShrink:0 }}/>:<div style={{ width:32,height:32,borderRadius:'50%',background:'var(--grad)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:800,flexShrink:0 }}>{profile?.name?.[0]||'?'}</div>}
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:12,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',color:'rgba(190,220,235,.8)' }}>{profile?.name}</div>
              <div style={{ fontSize:10,color:'rgba(80,120,150,.5)' }}>{isManager?'Manager':'Artist'}</div>
            </div>
            <button onClick={logout} style={{ background:'none',border:'none',cursor:'pointer',color:'rgba(80,120,150,.4)',padding:4 }}><LogOut size={14}/></button>
          </div>
        </div>
      </aside>
    </div>
  );
}